/**
 * The Daily Drop (04 §11): one mission per UTC day, the same for everyone, flown once by a
 * standard-issue "Ranger of the Day". The date arrives as a `YYYY-MM-DD` string - this layer
 * never reads a clock - and everything about the mission is derived from a hash of it.
 * Only the local table changes: no RP, no roster, no commendations, no records.
 */

import { Pcg32 } from '../sim/rng';
import type { Biome, MissionParams, ModifierId } from '../sim/types';
import type { Deployment } from './api';
import { blankRecord } from './company';
import { ARCHETYPES, type ArchetypeInfo, BIOMES } from './content/archetypes';
import { DAILY, DAILY_EXCLUDED, MONTH_DAYS } from './content/daily';
import { FIXED_POD, STANDARD_ISSUE } from './content/economy';
import pools from './content/name-pools.json';
import { STANDARD, stampDogTeams } from './dogs';
import { extraModifier, skyFor } from './opgen/dress';
import type { ProfileState } from './state';
import type { Aftermath, DailyEntry, DailyStatus, MissionSummary, RangerRecord } from './types';

/** A real calendar date written `YYYY-MM-DD`. */
export function validDate(text: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (month < 1 || month > 12 || day < 1) return false;
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  return day <= (MONTH_DAYS[month - 1] ?? 0) + (month === 2 && leap ? 1 : 0);
}

function checkDate(dateUtc: string): void {
  if (!validDate(dateUtc)) throw new Error('The Daily Drop needs a UTC date written YYYY-MM-DD.');
}

/**
 * May the Daily Drop fly this archetype (10 §4)? It is a DROP with a fixed kit - standard issue and `DAILY.pods`
 * fixed pods - so two things rule a mission out. One with no insertion (Prison Break): there is no drop, and
 * the standard issue the day promises is left behind with the ranger's freedom. And one whose mission item
 * rides in a pod the daily does not fly; with three pods every day and Two-Pod Limit never rolled, pods A-C
 * always fly, so the radio set and the strike beacon (both pod A) stay in.
 */
export function dailyEligible(info: ArchetypeInfo): boolean {
  const item = info.facts.questItem;
  return !info.facts.startCaptured && (item === null || item.pod < DAILY.pods);
}

/** What a day's mission is drawn from: every archetype the fixed kit can fly. */
export const DAILY_ARCHETYPES: readonly ArchetypeInfo[] = ARCHETYPES.filter(dailyEligible);

export interface DailyMission {
  params: MissionParams;
  firstName: string;
  surname: string;
}

/** The day's mission and its ranger: a pure function of the date string, each part from its own fork. */
export function dailyMission(dateUtc: string): DailyMission {
  checkDate(dateUtc);
  const root = new Pcg32(DAILY.seed, DAILY.stream).fork(dateUtc);
  const info = root.fork('archetype').pick(DAILY_ARCHETYPES);
  const place = root.fork('biome');
  const away = BIOMES.filter((b) => b !== info.native);
  const biome: Biome = place.chance(DAILY.nativeChance) ? info.native : place.pick(away);
  const threat = DAILY.minThreat + root.fork('threat').int(DAILY.threatSpread);
  const dress = root.fork('modifier');
  // The excluded modifiers are passed as already present, which is how the rules say "not this one".
  const rolled = dress.chance(DAILY.modifierChance) ? extraModifier(dress, info.id, DAILY_EXCLUDED, threat) : null;
  const modifiers: ModifierId[] = rolled ? [rolled] : [];
  const names = root.fork('ranger');
  const params: MissionParams = {
    archetype: info.id, biome, ...skyFor(root.fork('sky'), biome, modifiers), threat,
    seed: root.fork('map').nextU32(), modifiers, intel: [],
  };
  // ROUND8: the day's dog teams follow its rating at Standard, like Practice.
  stampDogTeams(params, STANDARD);
  return { params, firstName: names.pick(pools.firstNames), surname: names.pick(pools.surnames) };
}

const rangerName = (m: DailyMission): string => `${m.firstName} ${m.surname}`;

/** What the Daily Drop screen shows for a date: the mission, its ranger, and the day's line if it has been flown. */
export function dailyStatus(profile: ProfileState, dateUtc: string): DailyStatus {
  const mission = dailyMission(dateUtc);
  const played = profile.daily.find((e) => e.date === dateUtc) ?? null;
  return { date: dateUtc, params: mission.params, rangerName: rangerName(mission), played };
}

/**
 * The one attempt. The day goes on the table at once, as a scrub with no score, so quitting
 * can never buy a second try; `reportDaily` overwrites that line with what really happened.
 */
export function deployDaily(profile: ProfileState, dateUtc: string): Deployment {
  const mission = dailyMission(dateUtc);
  if (profile.daily.some((e) => e.date === dateUtc)) {
    throw new Error(`The Daily Drop for ${dateUtc} has been flown: one attempt a day.`);
  }
  const oldest = profile.daily[profile.daily.length - 1];
  if (profile.daily.length >= DAILY.keep && oldest && dateUtc < oldest.date) {
    throw new Error(`The Daily Drop for ${dateUtc} is too old for the table.`);
  }
  const entry: DailyEntry = {
    date: dateUtc, archetype: mission.params.archetype, biome: mission.params.biome, rangerName: rangerName(mission),
    outcome: 'scrubbed', score: 0, secondsUsed: 0,
  };
  // ISO dates sort as text: newest first, the oldest fall off the end.
  profile.daily = [entry, ...profile.daily].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, DAILY.keep);
  profile.dailyPending = dateUtc;
  return {
    params: mission.params, rangerId: DAILY.rangerId,
    loadout: { carried: { ...STANDARD_ISSUE }, pods: Array.from({ length: DAILY.pods }, () => ({ ...FIXED_POD })) },
    mods: {}, mode: 'daily',
  };
}

/** The real result replaces the day's provisional line. Nothing else in the profile moves. */
export function reportDaily(profile: ProfileState, deployment: Deployment, summary: MissionSummary): Aftermath {
  const date = profile.dailyPending;
  const mission = date === null ? null : dailyMission(date);
  const entry = profile.daily.find((e) => e.date === date);
  if (!mission || !entry || mission.params.seed !== deployment.params.seed) {
    throw new Error('That Daily Drop is not the one in the air.');
  }
  entry.outcome = summary.outcome;
  entry.score = summary.outcome === 'scrubbed' ? 0 : Math.max(0, Math.round(summary.score));
  entry.secondsUsed = Math.max(0, Math.round(summary.secondsUsed ?? 0));
  profile.dailyPending = null;
  const ranger = blankRecord(DAILY.rangerId, mission.params.seed, mission.firstName, mission.surname, DAILY.callsign);
  return unchangedAftermath(profile, ranger);
}

/** What Practice and the Daily Drop report: nobody on any roster was at risk and nothing was earned. */
export function unchangedAftermath(profile: ProfileState, ranger: RangerRecord): Aftermath {
  return {
    ranger, meritGained: 0, rpGained: 0, promotedTo: null, perkChoices: null, medals: [], readinessChange: 0,
    readinessReasons: [], freedRangers: [], memorial: null, operationStatus: profile.operation?.status ?? 'active',
  };
}
